import ReactPlayerLoader from "@brightcove/react-player-loader"
import styled from "@emotion/styled"
import { library } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPlay,
  faTimes,
} from "@fortawesome/free-solid-svg-icons"
import { DialogContent, DialogOverlay } from "@reach/dialog"
import "@reach/dialog/styles.css"
import { get } from "lodash"
import "objectFitPolyfill/dist/objectFitPolyfill.basic.min"
import React, { useEffect, useRef, useState } from "react"
import tw from "twin.macro"
import Picture from "./Picture"

library.add(
  faPlay,
  faTimes,
)

const ResponsiveWrapper = styled.div`
  ${tw`w-full relative`}
  ${(props) => {
    if (props.cover) {
      return tw`inset-0 absolute w-full h-full`
    } else {
      return `padding-bottom: 56.25%;`
    }
  }}
  .laitram-bc-player {
    ${tw`inset-0 absolute w-full h-full`}
    .video-js {
      ${tw`inset-0 absolute h-full w-full`}
    }
    .vjs-dock-text {
      ${tw`hidden`}
    }
  }
  ${(props) =>
    props.cover &&
    `
    .vjs-tech {
      object-fit: cover;
      object-position: 50% 50%;
    }
    .vjs-poster {
      background-size: cover;
    }

    /* Hide the player control bar */
    .video-js .vjs-control-bar {
      display: none;
    }

    /* Remove click events on the player */
    .video-js {
      pointer-events: none;
    }
  `}
`

const CoverImage = styled.div`
  ${tw`inset-0 absolute h-full w-full z-20 cursor-pointer`}
  ${(props) =>
    props.isPlaying && props.hideWhenPlaying && tw`hidden`}
  .laitram-bc-cover {
    ${tw`inset-0 absolute h-full w-full z-10`}
  }
`

const PlayButton = styled.button`
  ${tw`absolute z-30 rounded-full w-24 h-24 text-white`}
  background-color: rgba(0,0,0,.5);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  svg {
    ${tw`absolute`}
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

const CloseButton = styled.button`
  ${tw`absolute right-0 text-gray bg-white rounded-full w-6 h-6 mb-1 hover:bg-gray-light transition-colors duration-150`}
  bottom: 100%;
  svg {
    ${tw`absolute`}
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

const StyledDialogOverlay = styled(DialogOverlay)`
  ${tw`z-30`}
  background: "hsla(0, 100%, 100%, 0.9)";
`

const StyledDialogContent = styled(DialogContent)`
  ${tw`p-4 z-40 shadow-lg relative max-w-full`}
  width: 95%;
  @media (min-width: 800px) {
    width: 720px;
  }
`

const VideoEmbed = ({ video, play, playInModal, cover, ...attrs }) => {
  const brightcoveId = get(video, "elements.brightcove_id.value")
  const coverImage = get(
    video,
    "elements.cover_image.value[0].elements.image.value[0]"
  )
  const altText = get(video, "elements.alt_text.value")
  const [showDialog, setShowDialog] = useState(false)
  const closeModal = () => setShowDialog(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [player, setPlayer] = useState()

  const ref = useRef()

  const handleCoverImageClick = () => {
    if (playInModal) {
      setShowDialog(true)
    }
  }

  const handleModalDismiss = () => {
    closeModal()
    if (!player) {
      return
    }
    player.pause()
  }

  useEffect(() => {
    if (!player) {
      return
    }

    // Works around object-fit issue when video banners load
    let objectFitTimer

    if (cover) {
      const updateObjectFit = () => {
        const bannerVideos = ref.current.querySelectorAll(".vjs-tech")
        objectFitPolyfill(bannerVideos) // eslint-disable-line
      }

      objectFitTimer = setTimeout(updateObjectFit, 1000)
    }

    const handlePlayPause = () => {
      if (play === true) {
        const playerPlay = player.play()

        if (playerPlay !== undefined) {
          playerPlay
            .then(function () {
              // play started
            })
            .catch(function (error) {
              // play was prevented
              console.log(error)
            })
        }
      }

      if (play === false) {
        const playerPause = player.pause()

        if (playerPause !== undefined) {
          playerPause
            .then(function () {
              // player paused
            })
            .catch(function (error) {
              // pause was prevented
              console.log(error)
            })
        }
      }
    }

    handlePlayPause()

    player.on("loadedmetadata", handlePlayPause)

    return () => {
      if (objectFitTimer) {
        clearTimeout(objectFitTimer)
      }
    }
  }, [player, play, cover])

  const onBrightcoveSuccess = (success) => {
    // https://docs.brightcove.com/brightcove-player/1.x/Player.htm
    setPlayer(success.ref)

    const handlePlay = () => setIsPlaying(true)
    const handleEnded = () => setIsPlaying(false)

    success.ref.on("play", handlePlay)
    success.ref.on("ended", handleEnded)

    if (cover) {
      const videoEl = success.ref.el().querySelector(".vjs-tech")
      videoEl.dataset.objectFit = "cover"
    }

    if (play === true || showDialog === true) {
      success.ref.play()
    }

    if (play === false) {
      success.ref.pause()
    }
  }

  const videoMarkup = () => {
    return (
      <ReactPlayerLoader
        accountId={process.env.GATSBY_BRIGHTCOVE_ACCOUNT_ID}
        playerId={process.env.GATSBY_BRIGHTCOVE_PLAYER_ID}
        videoId={brightcoveId}
        attrs={{ className: "laitram-bc-player" }}
        onSuccess={onBrightcoveSuccess}
        options={attrs}
      />
    )
  }

  return (
    <>
      <ResponsiveWrapper cover={cover} ref={ref}>
        {coverImage || playInModal ? (
          <CoverImage
            isPlaying={isPlaying}
            hideWhenPlaying={!playInModal}
            onClick={handleCoverImageClick}
          >
            <PlayButton>
              <FontAwesomeIcon icon={["fas", "play"]} size="2x" />
            </PlayButton>
            {coverImage ? (
              <Picture
                image={coverImage}
                className={`laitram-bc-cover`}
                cropWidth={600}
                cropRatio={0.5375}
                cover={true}
                alt=""
              />
            ) : null}
          </CoverImage>
        ) : null}
        {!playInModal ? videoMarkup() : null}
      </ResponsiveWrapper>
      {playInModal ? (
        <StyledDialogOverlay isOpen={showDialog} onDismiss={handleModalDismiss}>
          <StyledDialogContent aria-label={altText}>
            <CloseButton aria-label="Close" onClick={closeModal}>
              <FontAwesomeIcon icon={["fas", "times"]} />
            </CloseButton>
            <ResponsiveWrapper>{videoMarkup(attrs)}</ResponsiveWrapper>
          </StyledDialogContent>
        </StyledDialogOverlay>
      ) : null}
    </>
  )
}

export default VideoEmbed
